import { Injectable, computed, signal } from '@angular/core';
import { formatDate } from '@angular/common';
import { TenantBillingSummaryDto, BillingAlert } from './billing.service';

export interface SubscriptionBlock {
  reason: 'SUBSCRIPTION_INACTIVE' | 'TENANT_SUSPENDED';
  message: string;
  planStatus?: string;
  tenantStatus?: string;
  graceEndDate?: string;
  /** Grace window over: every request except billing is refused. */
  accessRestricted?: boolean;
}

/** What the header badge and the shell banner say about the renewal clock. */
export interface RenewalNotice {
  kind: 'expiring' | 'grace' | 'ended';
  /** Short text for the header badge. */
  label: string;
  /** Full sentence(s) for the banner. */
  message: string;
  /** yyyy-MM-dd shown beside the label (plan end while expiring, grace end otherwise). */
  date: string | null;
}

/**
 * The app's single view of "can this tenant still change things, and if not
 * why".
 *
 * Two independent sources feed it, and both matter:
 *
 *  · the billing summary, fetched when the billing screen loads — the planned,
 *    complete picture;
 *  · a refusal caught by subscriptionInterceptor — the unplanned one, which is
 *    how any OTHER screen first learns the subscription has lapsed mid-session.
 *
 * Kept as signals so the shell banner re-renders without every screen having to
 * subscribe to anything, matching the codebase's existing state approach.
 *
 * <b>This is a mirror of server state, never a substitute for it.</b> Nothing
 * here decides whether an action is allowed — the API refuses independently.
 * Its only job is to explain a refusal the user would otherwise experience as an
 * unexplained failure.
 */
@Injectable({ providedIn: 'root' })
export class SubscriptionStateService {
  private readonly _summary = signal<TenantBillingSummaryDto | null>(null);
  private readonly _block   = signal<SubscriptionBlock | null>(null);

  readonly summary = this._summary.asReadonly();
  readonly block   = this._block.asReadonly();

  /**
   * True only when we have positive evidence of a block. Unknown is treated as
   * allowed on purpose: the server is the gate, and greying the whole UI out
   * because a summary call has not returned yet would be worse than letting a
   * doomed request fail honestly.
   */
  readonly isBlocked = computed(() =>
    this._block() !== null || this._summary()?.allowsWrites === false);

  readonly blockMessage = computed(() =>
    this._block()?.message ?? this._summary()?.blockReason ?? null);

  /** What the sidebar says in place of the modules once the grace period is over. */
  readonly restrictionMessage = computed(() =>
    this.renewalNotice()?.kind === 'ended'
      ? this.renewalNotice()!.message
      : this._block()?.message
        ?? 'Your Grace Period has expired. Please renew your plan to continue using BookingHub.');

  /** True when paying would restore access — false for an admin suspension. */
  readonly canFixByPaying = computed(() => {
    const b = this._block();
    if (b) return b.reason === 'SUBSCRIPTION_INACTIVE';
    const s = this._summary();
    return s ? s.allowsWrites === false && s.blockReason !== 'TENANT_SUSPENDED' : false;
  });

  /** False = this tenant is not forced to renew: no badge, no banner, no restriction. Absent = true. */
  readonly isRenewalRequired = computed(() => this._summary()?.isRenewalRequired !== false);

  /** Grace is over and the API is refusing everything except billing — from the summary or a refused request. */
  readonly isRestricted = computed(() =>
    this._summary()?.isRenewalRequired !== false
    && (this._summary()?.isAccessRestricted === true || this._block()?.accessRestricted === true));

  /**
   * Everything the badge and banner say, derived from the server's own flags and dates — nothing is
   * calculated here, so the text follows the server's view of "today" and the current grace setting.
   * Null when there is nothing to say (renewal not required, plan current, or no summary yet).
   */
  readonly renewalNotice = computed<RenewalNotice | null>(() => {
    const s = this._summary();
    if (!s || s.isRenewalRequired === false) return null;

    const fmt = (d?: string | null) => (d ? formatDate(d, 'dd MMM yyyy', 'en-US') : '');
    const days = s.graceDaysRemaining ?? null;

    if (s.isAccessRestricted) {
      const over = days != null ? Math.abs(days) : null;
      return {
        kind: 'ended',
        label: over != null
          ? (over === 1 ? 'Grace period ended yesterday' : `Grace period ended ${over} days ago`)
          : 'Grace period ended',
        message: 'Your Grace Period has expired. Please renew your plan to continue using BookingHub.',
        date: s.gracePeriodEndDate ?? null
      };
    }

    if (s.isInGracePeriod || s.alerts?.includes('GRACE_PERIOD')) {
      return {
        kind: 'grace',
        label: days == null ? 'Grace period'
          : days <= 0 ? 'Grace period ends today'
          : days === 1 ? 'Grace period: 1 day left' : `Grace period: ${days} days left`,
        message: 'Your plan has expired. You are currently in the Grace Period.'
          + (s.gracePeriodEndDate ? ` Grace Period ends on ${fmt(s.gracePeriodEndDate)}.` : ''),
        date: s.gracePeriodEndDate ?? null
      };
    }

    // Expired with no grace window to report -- e.g. a trial that ran out, which has no plan end
    // date. Still a renewal the tenant must act on, so the header shows it too.
    if (s.alerts?.includes('EXPIRED')) {
      return {
        kind: 'ended',
        label: 'Plan expired',
        message: 'Your plan has expired. Please renew your plan to continue using BookingHub.',
        date: s.periodEndDate ?? s.trialEndDate ?? null
      };
    }

    if (s.alerts?.includes('EXPIRING_SOON') && s.periodEndDate) {
      const left = s.daysRemaining ?? null;
      return {
        kind: 'expiring',
        label: left == null ? 'Plan expiring'
          : left <= 0 ? 'Plan expires today'
          : left === 1 ? 'Plan expires in 1 day' : `Plan expires in ${left} days`,
        message: `Your plan will expire on ${fmt(s.periodEndDate)}.`,
        date: s.periodEndDate
      };
    }

    return null;
  });

  readonly alerts = computed<BillingAlert[]>(() => this._summary()?.alerts ?? []);

  /** The most severe alert, for a shell banner that shows only one. */
  readonly topAlert = computed<BillingAlert | null>(() => this.alerts()[0] ?? null);

  setSummary(summary: TenantBillingSummaryDto | null): void {
    this._summary.set(summary);

    // A summary that says writes are allowed clears any stale block: the tenant
    // has paid, or an admin reactivated them, and leaving the banner up would
    // tell them to pay for something they have already paid for.
    if (summary?.allowsWrites) this._block.set(null);
  }

  reportBlocked(block: SubscriptionBlock): void {
    this._block.set(block);
  }

  /** Called on logout — the next user may belong to a healthy tenant. */
  clear(): void {
    this._summary.set(null);
    this._block.set(null);
  }
}
